import { Activity, ArrowRight, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type AuditLogEntry, type FieldChange, useAuditLog } from "@/hooks/use-audit-log";
import { actorLabel } from "@/lib/audit-actor-label";
import { formatEffectiveDate, formatRelative } from "@/lib/dates";
import { cn } from "@/lib/utils";

interface ActivityLogProps {
  entityType: string;
  entityId: string;
  /**
   * Optional map of FK column name → resolver. When a diff change is for one
   * of these columns, the resolver is called to produce a display string
   * (typically a name pulled from the parent record). Without a resolver the
   * raw value is rendered.
   *
   * Example: `{ classificationId: (id) => classifications.find(c => c.id === id)?.name ?? id }`.
   */
  fieldResolvers?: Record<string, (value: unknown) => string>;
  /**
   * Optional display labels for fields (e.g. "Job title" instead of "title").
   * Defaults to a humanized form of the field name.
   */
  fieldLabels?: Record<string, string>;
}

/**
 * Per-record activity log. Reads from `/api/audit-log` via `useAuditLog` and
 * renders a vertical timeline of entries with relative timestamps and (for
 * updates) a field-by-field diff.
 *
 * Suppress the entire section if the API call fails — the activity log is
 * supplementary; a failure shouldn't break the detail page.
 */
export function ActivityLog({
  entityType,
  entityId,
  fieldResolvers,
  fieldLabels,
}: ActivityLogProps) {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useAuditLog(
    entityType,
    entityId,
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return <p className="text-sm text-muted-foreground">Activity unavailable.</p>;
  }

  const entries = data.pages.flatMap((page) => page.data);

  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No activity recorded yet.</p>;
  }

  return (
    <div className="space-y-4">
      <ol className="space-y-4">
        {entries.map((entry) => (
          <ActivityEntry
            key={entry.id}
            entry={entry}
            fieldResolvers={fieldResolvers}
            fieldLabels={fieldLabels}
          />
        ))}
      </ol>
      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </div>
  );
}

/**
 * Icon + color tone for an audit action. Exported so the person Change Timeline
 * can render matching node dots (`dot`, a `bg-*` class) alongside the same
 * iconography the flat log uses (`Icon` + `tone`, a `text-*` class).
 */
export function activityVisuals(action: AuditLogEntry["action"]): {
  Icon: typeof Activity;
  tone: string;
  dot: string;
} {
  if (action === "created") return { Icon: Plus, tone: "text-success", dot: "bg-success" };
  if (action === "deleted")
    return { Icon: Trash2, tone: "text-destructive", dot: "bg-destructive" };
  return { Icon: Activity, tone: "text-info", dot: "bg-info" };
}

function ActivityEntry({
  entry,
  fieldResolvers,
  fieldLabels,
}: {
  entry: AuditLogEntry;
  fieldResolvers?: Record<string, (value: unknown) => string>;
  fieldLabels?: Record<string, string>;
}) {
  const { Icon, tone } = activityVisuals(entry.action);

  return (
    <li className="flex gap-3">
      <div className={cn("mt-0.5 shrink-0", tone)}>
        <Icon className="h-4 w-4" />
      </div>
      <ActivityEntryContent
        entry={entry}
        fieldResolvers={fieldResolvers}
        fieldLabels={fieldLabels}
      />
    </li>
  );
}

/**
 * The textual body of one audit entry — actor line, per-field diff, and change
 * reason. Split out of `ActivityEntry` so the flat list keeps its structure;
 * the person Change Timeline renders its own actor-free variant.
 */
function ActivityEntryContent({
  entry,
  fieldResolvers,
  fieldLabels,
}: {
  entry: AuditLogEntry;
  fieldResolvers?: Record<string, (value: unknown) => string>;
  fieldLabels?: Record<string, string>;
}) {
  return (
    <div className="flex-1 space-y-1">
      <p className="text-sm">
        <span className="font-medium">{actorLabel(entry)}</span>{" "}
        <span className="text-muted-foreground">{actionPhrase(entry)}</span>{" "}
        <span className="text-muted-foreground" title={new Date(entry.createdAt).toLocaleString()}>
          {formatRelative(new Date(entry.createdAt))}
        </span>
      </p>
      {entry.action === "updated" && Array.isArray(entry.diff) && (
        <ul className="space-y-0.5 text-xs">
          {(entry.diff as FieldChange[]).map((change) => (
            <li key={change.field} className="text-muted-foreground">
              <span className="font-medium text-foreground">
                {fieldLabelFor(change.field, fieldLabels)}
              </span>
              {": "}
              <ChangeValue value={change.before} field={change.field} resolvers={fieldResolvers} />
              <ArrowRight className="mx-1 inline h-3 w-3" />
              <ChangeValue value={change.after} field={change.field} resolvers={fieldResolvers} />
            </li>
          ))}
        </ul>
      )}
      {entry.changeReasonName && (
        <p className="text-xs text-muted-foreground">
          Reason: <span className="font-medium text-foreground">{entry.changeReasonName}</span>
        </p>
      )}
    </div>
  );
}

export function ChangeValue({
  value,
  field,
  resolvers,
}: {
  value: unknown;
  field: string;
  resolvers?: Record<string, (value: unknown) => string>;
}) {
  if (value === null || value === undefined || value === "") {
    return <span className="italic">empty</span>;
  }
  const resolved = resolvers?.[field]?.(value);
  if (resolved !== undefined) return <span>{resolved}</span>;
  if (typeof value === "object") {
    return <span className="font-mono">{JSON.stringify(value)}</span>;
  }
  return <span>{String(value)}</span>;
}

function actionPhrase(entry: AuditLogEntry): string {
  if (entry.action === "created") return "created this record";
  if (entry.action === "deleted") return "deleted this record";
  const count = Array.isArray(entry.diff) ? entry.diff.length : 0;
  const base =
    count === 0 ? "saved this record" : count === 1 ? "updated 1 field" : `updated ${count} fields`;
  return entry.effectiveDate
    ? `${base} (effective ${formatEffectiveDate(entry.effectiveDate)})`
    : base;
}

function humanizeField(field: string): string {
  return field
    .replace(/Id$/, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Fields whose label is the same wherever they appear, and which humanizing
 * would render badly. A caller's own map still wins.
 */
const SHARED_FIELD_LABELS: Record<string, string> = {
  // Written by `noteAdminEdit` when an admin edits somebody else's review.
  // The value names which half was touched, never what was written.
  editedByAdmin: "Edited by an admin",
  // Notification Templates: the on/off switch, and who was skipped on switching back on.
  isActive: "Switched on",
  skippedPeople: "People skipped (not emailed)",
};

/**
 * Display label for a diff field. A "canceled:<field>" entry marks a withdrawn
 * scheduled change (see cancelPendingChange server-side) and renders as
 * "Canceled scheduled change — <label>"; everything else uses the supplied
 * label map with a humanized fallback.
 */
export function fieldLabelFor(field: string, fieldLabels?: Record<string, string>): string {
  if (field.startsWith("canceled:")) {
    const bare = field.slice("canceled:".length);
    return `Canceled scheduled change — ${fieldLabels?.[bare] ?? humanizeField(bare)}`;
  }
  return fieldLabels?.[field] ?? SHARED_FIELD_LABELS[field] ?? humanizeField(field);
}
